import React, {createContext, useContext, useMemo} from 'react'

interface ProductionPlanningHostContextValue {
  /**
   * Optional slot rendered next to the production-planning page title.
   *
   * HCEM injects its tour-triggering optimizer notification here (see hcem
   * `OptimizerNotification`); Suite leaves it undefined so nothing is rendered.
   */
  HeaderNotification?: React.ComponentType<{plantCode: string}>
  /**
   * Whether the feature is rendered on the legacy blue (dark) surface.
   *
   * HCEM sets `true` so header controls use their white-on-blue styling; Suite
   * leaves it `false` so controls follow the (light/dark) theme tokens.
   */
  inverted?: boolean
}

const ProductionPlanningHostContext = createContext<ProductionPlanningHostContextValue>({})

export interface ProductionPlanningHostProviderProps {
  children: React.ReactNode
  HeaderNotification?: React.ComponentType<{plantCode: string}>
  inverted?: boolean
}

/**
 * Provider that lets the host app inject production-planning behaviour that differs
 * between HCEM and Suite. Mirrors the storage-levels host-context pattern
 * (`StorageLevelsRouteProvider` / `MaterialOrderPermissionsProvider`).
 */
export const ProductionPlanningHostProvider: React.FC<ProductionPlanningHostProviderProps> = ({
  children,
  HeaderNotification,
  inverted = false
}) => {
  const value = useMemo(() => ({HeaderNotification, inverted}), [HeaderNotification, inverted])
  return (
    <ProductionPlanningHostContext.Provider value={value}>
      {children}
    </ProductionPlanningHostContext.Provider>
  )
}

/**
 * Read host-injected production-planning behaviour. Falls back to safe defaults
 * (no injected slots) when no provider is present, so the feature works standalone.
 */
export const useProductionPlanningHost = (): ProductionPlanningHostContextValue =>
  useContext(ProductionPlanningHostContext)
